/**
 * Numbering follows the boxes on the ICS 203-CG form, as printed by
 * `services/coms/src/routes/Forms/ICS_CG/forms/ICS203CG.ts`.
 *
 * The printed sheet runs two columns below the header: boxes 4 to 7 on the
 * left, 8 to 10 on the right, with box 11 spanning the foot. Boxes 1 to 3 are
 * read-only, filled from the incident and its operational period.
 */

import { ICS203_POSITION_KEYS } from 'constants/ics203PositionKeys';

export const ICS203_BOX_LABELS = {
  incidentName: '1. Incident Name:',
  incidentLocation: '2. Incident Location:',
  operationalPeriod: '3. Operational Period (Date/Time):',
  commandStaff: '4. Incident Commanders and Command Staff:',
  agencyRepresentatives: '5. Agency / Organization Representatives:',
  planningSection: '6. Planning Section:',
  logisticsSection: '7. Logistics Section:',
  operationsSection: '8. Operations Section:',
  intelligenceSection: '9. Intelligence / Investigations Section:',
  financeSection: '10. Finance / Administration Section:',
  preparedBy: '11. Prepared by:',
} as const;

/**
 * One labelled blank inside a section box.
 *
 * `positionKey` is the row of `ics_incident_organization_assignments` the blank
 * reads and writes, which is also what the PDF export looks the value up by, so
 * a key renamed here stops printing.
 */
export type Ics203Position = {
  label: string;
  positionKey: string;
};

/** The three blanks the command staff box prints below its commander table. */
export const ICS203_COMMAND_STAFF: readonly Ics203Position[] = [
  { label: 'Safety Officer', positionKey: 'Safety Officer' },
  { label: 'Information Officer', positionKey: 'Public Information Officer' },
  { label: 'Liaison Officer', positionKey: 'Liaison Officer' },
];

export const ICS203_PLANNING_SECTION: readonly Ics203Position[] = [
  { label: 'Chief', positionKey: 'Planning Section Chief' },
  { label: 'Deputy', positionKey: ICS203_POSITION_KEYS.planningSectionDeputy },
  { label: 'Resources Unit', positionKey: 'Resource Unit Leader' },
  { label: 'Situation Unit', positionKey: 'Situation Unit Leader' },
  { label: 'Environmental Unit', positionKey: 'Environmental Unit Leader' },
  {
    label: 'Documentation Unit',
    positionKey: ICS203_POSITION_KEYS.documentationUnitLeader,
  },
  {
    label: 'Demobilization Unit',
    positionKey: ICS203_POSITION_KEYS.demobilizationUnitLeader,
  },
  {
    label: 'Technical Specialists',
    positionKey: ICS203_POSITION_KEYS.technicalSpecialists,
  },
];

/**
 * The logistics box, which the printed form shades into two branches.
 *
 * `branch` marks the shaded Support Branch and Service Branch header rows; the
 * blanks after each belong to it until the next one.
 */
export type Ics203LogisticsRow = Ics203Position & { branch?: boolean };

export const ICS203_LOGISTICS_SECTION: readonly Ics203LogisticsRow[] = [
  { label: 'Chief', positionKey: ICS203_POSITION_KEYS.logisticSectionChief },
  { label: 'Deputy', positionKey: ICS203_POSITION_KEYS.logisticSectionDeputy },
  {
    label: 'Support Branch',
    positionKey: ICS203_POSITION_KEYS.supportBranchName,
    branch: true,
  },
  {
    label: 'Director',
    positionKey: ICS203_POSITION_KEYS.supportBranchDirector,
  },
  { label: 'Supply Unit', positionKey: ICS203_POSITION_KEYS.supplyUnitLeader },
  {
    label: 'Facilities Unit',
    positionKey: ICS203_POSITION_KEYS.facilitiesUnitLeader,
  },
  {
    label: 'Service Branch',
    positionKey: ICS203_POSITION_KEYS.serviceBranchName,
    branch: true,
  },
  {
    label: 'Director',
    positionKey: ICS203_POSITION_KEYS.serviceBranchDirector,
  },
  {
    label: 'Communications Unit',
    positionKey: ICS203_POSITION_KEYS.communicationUnitLeader,
  },
  {
    label: 'Medical Unit',
    positionKey: ICS203_POSITION_KEYS.medicalUnitLeader,
  },
];

/** The blanks box 8 prints above its branch and division rows. */
export const ICS203_OPERATIONS_SECTION: readonly Ics203Position[] = [
  { label: 'Chief', positionKey: ICS203_POSITION_KEYS.operationSectionChief },
  { label: 'Deputy', positionKey: ICS203_POSITION_KEYS.operationSectionDeputy },
  {
    label: 'Staging Area Manager',
    positionKey: ICS203_POSITION_KEYS.stagingAreaManager1,
  },
  {
    label: 'Staging Area Manager',
    positionKey: ICS203_POSITION_KEYS.stagingAreaManager2,
  },
  {
    label: 'Staging Area Manager',
    positionKey: ICS203_POSITION_KEYS.stagingAreaManager3,
  },
];

export const ICS203_INTELLIGENCE_SECTION: readonly Ics203Position[] = [
  { label: 'Chief', positionKey: ICS203_POSITION_KEYS.intelligenceUnitLeader },
  { label: 'Deputy', positionKey: ICS203_POSITION_KEYS.intelligenceDeputy },
];

export const ICS203_FINANCE_SECTION: readonly Ics203Position[] = [
  { label: 'Chief', positionKey: ICS203_POSITION_KEYS.financeSectionChief },
  { label: 'Deputy', positionKey: ICS203_POSITION_KEYS.financeSectionDeputy },
  { label: 'Time Unit', positionKey: ICS203_POSITION_KEYS.timeUnitLeader },
  {
    label: 'Procurement Unit',
    positionKey: ICS203_POSITION_KEYS.procurementUnitLeader,
  },
  {
    label: 'Comp / Claims Unit',
    positionKey: ICS203_POSITION_KEYS.compensationUnitLeader,
  },
  { label: 'Cost Unit', positionKey: ICS203_POSITION_KEYS.costUnitLeader },
];

/** The three columns box 4's commander table prints. */
export const ICS203_COMMANDER_COLUMNS = [
  { label: 'Incident Commander', source: 'incident roster' },
  { label: 'Deputy', source: '<seat> Deputy' },
  { label: 'Agency', source: '<seat> Agency' },
] as const;

/** The two columns box 5 prints. */
export const ICS203_AGENCY_COLUMNS = [
  { label: 'Agency / Organization', source: 'agency_name' },
  { label: 'Name', source: 'representative_name' },
] as const;

export const ICS203_INCIDENT_NAME_HINT =
  'This box is read-only. It is filled from the incident name.';

export const ICS203_INCIDENT_LOCATION_HINT =
  'This box is read-only. It is filled from the incident location, falling back to the address set when the incident was created.';

export const ICS203_OPERATIONAL_PERIOD_HINT =
  'Click this box to set the scheduled start and end of the next operational period for this incident workspace. Working-period forms print that window.';

/**
 * The two tooltips the Bootstrap card carried, kept word for word.
 *
 * Both are load-bearing: the first answers the question the card invites by
 * listing roster seats it will not write back to, and the second marks the
 * blanks a planner cannot type into. Playwright reads the second off an
 * `aria-label`, so the wording is also a selector.
 */
export const ICS203_HEADER_DOC_TOOLTIP =
  'This form does not update the Incident Roster and is for documentation purposes only.';

export const ICS203_ROSTER_FIELD_TOOLTIP =
  'This value is automatically populated with the user(s) in Incident Roster.';

export const ICS203_COMMAND_STAFF_HINT =
  'Commanders are listed from the Incident Roster, so a row appears once someone holds that seat. Deputy and Agency are typed here.';

export const ICS203_AGENCY_HINT =
  'The printed form has four agency rows, so the card stops at four.';

export const ICS203_OPERATIONS_HINT =
  'Branches and their divisions are shared with the assignment list, so a branch added here shows up on the 204 as well.';
